import { useEffectiveRole, COACH_ROLES, ROLE_LABELS } from "@/lib/permissions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Eye, X } from "lucide-react";

/**
 * Inline toggle dropdown — appears inside StaffCard for Head Coaches only.
 * Lets them switch the rendered UI to "view as" another role.
 */
export function ShadowLogToggle() {
  const { role, isShadow, real, set } = useEffectiveRole();
  if (real !== "head_coach") return null;
  return (
    <div className="rounded-xl p-4 mt-5 border" style={{ borderColor: isShadow ? "rgba(245,158,11,0.5)" : "var(--ink-border)", backgroundColor: isShadow ? "rgba(245,158,11,0.08)" : "var(--ink-1)" }} data-testid="shadow-log-toggle">
      <div className="flex items-center gap-3 flex-wrap">
        <Eye className="h-4 w-4" style={{ color: isShadow ? "#FCD34D" : "var(--brand-secondary)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: isShadow ? "#FCD34D" : "var(--brand-secondary)" }}>Shadow-log mode</div>
          <p className="text-xs text-white/60 mt-0.5">See exactly what each staff role sees. UI mask only — backend permissions still enforce.</p>
        </div>
        <Select value={role} onValueChange={(v) => set(v === real ? null : v)}>
          <SelectTrigger className="w-48 bg-[var(--ink-2)] border-[var(--ink-border)] text-white text-xs" data-testid="shadow-log-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COACH_ROLES.map((k) => (
              <SelectItem key={k} value={k}>{ROLE_LABELS[k]}{k === real ? " (you)" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isShadow && (
          <Button size="sm" variant="outline" onClick={() => set(null)} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="shadow-log-exit-inline">
            <X className="h-3.5 w-3.5 mr-1.5" /> Exit
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Sticky banner at the top of every coach page when view-as is active.
 * Inserted in CoachLayout.
 */
export function ShadowBanner() {
  const { role, isShadow, set } = useEffectiveRole();
  if (!isShadow) return null;
  return (
    <div className="w-full text-black flex items-center justify-center gap-3 px-4 py-2 text-xs font-bold" style={{ backgroundColor: "#FCD34D" }} data-testid="shadow-log-banner">
      <Eye className="h-3.5 w-3.5" />
      <span className="tracking-[0.1em] uppercase">Viewing as {ROLE_LABELS[role]}</span>
      <button onClick={() => set(null)} className="ml-2 inline-flex items-center gap-1 underline hover:no-underline" data-testid="shadow-log-exit-banner">
        Exit shadow mode <X className="h-3 w-3" />
      </button>
    </div>
  );
}
